import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BLOG_FALLBACK_COVER } from '../utils/seo';

const EnhancedRelatedPosts = ({ relatedPosts, currentPost }) => {
  const [expanded, setExpanded] = useState(false);

  if (!relatedPosts || relatedPosts.length === 0) return null;

  const displayedPosts = expanded ? relatedPosts : relatedPosts.slice(0, 3);

  const getReadingTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = content ? content.split(' ').length : 0;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `${minutes} min read`;
  };

  const getCategoryColor = (tags) => {
    if (!tags || tags.length === 0) return 'bg-[#e7e4df] text-[#2d2b29]';
    return 'bg-[#e7e4df] text-[#2d2b29]';
  };

  return (
    <div
      className="border border-stone-200 bg-white/70 p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <h2 className="font-display text-2xl text-[#2d2b29]">Related reading</h2>
        </div>
        <span className="text-xs uppercase tracking-[0.2em] text-[#7b7671]">
          {relatedPosts.length} articles
        </span>
      </div>

      <div className="grid gap-6">
        {displayedPosts.map((post, index) => (
          <div
            key={post.slug}
          >
            <Link
              to={`/blog/${post.slug}`}
              className="block group"
            >
              <div className="flex gap-4 p-4 border border-stone-200 hover:shadow-sm transition-all duration-200">
                {/* Image */}
                <div className="flex-shrink-0">
                  <img
                    src={post.cover || BLOG_FALLBACK_COVER}
                    alt={`Cover for ${post.title}`}
                    className="w-20 h-20 object-cover image-editorial"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = BLOG_FALLBACK_COVER;
                    }}
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    {post.tags && post.tags.length > 0 && (
                      <span className={`px-2 py-1 text-[0.65rem] uppercase tracking-[0.2em] ${getCategoryColor(post.tags)}`}>
                        {post.tags[0]}
                      </span>
                    )}
                    <span className="text-xs text-[#7b7671] uppercase tracking-[0.2em]">
                      {getReadingTime(post.content)}
                    </span>
                  </div>

                  <h3 className="font-display text-lg text-[#2d2b29] group-hover:text-[#1f1d1b] transition-colors mb-2 line-clamp-2">
                    {post.title}
                  </h3>

                  <p className="text-sm text-[#6a6661] line-clamp-2 mb-2">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-xs text-[#7b7671] uppercase tracking-[0.2em]">
                      <span>{new Date(post.date).toLocaleDateString()}</span>
                      {post.author && <span>{post.author}</span>}
                    </div>
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex-shrink-0 flex items-center">
                  <svg 
                    className="w-5 h-5 text-[#b1aba3] group-hover:text-[#2d2b29] group-hover:translate-x-1 transition-all duration-200" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>

      {/* Show More/Less Button */}
      {relatedPosts.length > 3 && (
        <div className="mt-6 text-center">
          <button
            onClick={() => setExpanded(!expanded)}
            className="px-6 py-2 border border-stone-200 text-xs uppercase tracking-[0.25em] text-[#6b6660] hover:text-[#2d2b29] transition-colors"
          >
            {expanded ? 'Show Less' : `Show ${relatedPosts.length - 3} More Articles`}
          </button>
        </div>
      )}
    </div>
  );
};

export default EnhancedRelatedPosts; 
